import { useToastRegion, useToast } from '@react-aria/toast';
import { ToastQueue, useToastQueue } from '@react-stately/toast';
import { AnimatePresence, useMotionValue, useTransform, motion, animate } from 'motion/react';
import React__default, { useRef, useId } from 'react';
import { CloseButton } from '../atoms/Button.js';
import './Toast.scss';

// Global toast queue
const globalToastQueue = new ToastQueue({
    maxVisibleToasts: 10, // Increased to allow multiple placements
});
/**
 * ToastProvider manages the state for the toast queue.
 * Wrap your app with this component to enable toast notifications.
 *
 * ```tsx
 * // 1. Wrap your app with ToastProvider
 * <ToastProvider>
 *   <App />
 * </ToastProvider>
 *
 * // 2. Import and call the toast function from anywhere
 * import { toast } from '@ekhodealer/component-library/rev';
 *
 * // Simple toast
 * toast({ message: 'Item saved!', variant: 'success' });
 *
 * // With title and placement
 * toast({
 *   title: 'Error',
 *   message: 'Something went wrong',
 *   variant: 'error',
 *   placement: 'bottom right',
 * });
 *
 * // With custom timeout (ms) or null to persist
 * toast({ message: 'Quick notice', variant: 'info', timeout: 3000 });
 * toast({ message: 'Manual dismiss only', variant: 'warning', timeout: null });
 *
 * // With custom children (e.g., action buttons)
 * toast({
 *   message: 'File deleted',
 *   variant: 'info',
 *   children: (closeToast) => (
 *     <Button onPress={() => { undo(); closeToast(); }}>Undo</Button>
 *   ),
 * });
 * ```
 *
 * @see ToastOptions for all available options
 * @see ToastVariant for variant types: 'success' | 'error' | 'warning' | 'info'
 * @see ToastPlacement for placement options: 'top' | 'top left' | 'top right' | 'bottom' | 'bottom left' | 'bottom right'
 */
const ToastProvider = ({ children }) => {
    const state = useToastQueue(globalToastQueue);
    return (React__default.createElement(React__default.Fragment, null,
        children,
        React__default.createElement(ToastRegion, { state: state })));
};
// All possible placements in a stable order
const PLACEMENTS = [
    'top left',
    'top',
    'top right',
    'bottom left',
    'bottom',
    'bottom right',
];
// Swipe-to-dismiss: returns exit direction or null if shouldn't dismiss
const getDismissDirection = (info, isTop) => {
    const { velocity, offset } = info;
    const speedX = Math.abs(velocity.x);
    const speedY = Math.abs(velocity.y);
    const distanceX = Math.abs(offset.x);
    const distanceY = Math.abs(offset.y);
    // Minimum 10px movement threshold to even consider dismissing
    if (distanceX < 10 && distanceY < 10) {
        return null;
    }
    // Check horizontal swipe (left or right)
    if (speedX > 500 || distanceX > 60) {
        return { x: offset.x > 0 ? 400 : -400, y: 0 };
    }
    // Check vertical swipe in correct direction
    const correctVertical = isTop ? offset.y < 0 : offset.y > 0;
    if (correctVertical && (speedY > 500 || distanceY > 60)) {
        return { x: 0, y: isTop ? -150 : 150 };
    }
    return null;
};
// Drag distance at which opacity reaches 0 and blur reaches max
const DRAG_DISMISS_THRESHOLD = 100;
const DraggableToastItem = ({ toast, state, isTop, initialY, exitDir, onDismiss, onAnimationComplete, }) => {
    const x = useMotionValue(0);
    const y = useMotionValue(0);
    // Calculate drag distance (max of absolute x or y)
    const dragDistance = useTransform(() => Math.max(Math.abs(x.get()), Math.abs(y.get())));
    // Opacity: 1 at 0px, 0 at threshold
    const opacity = useTransform(dragDistance, [0, DRAG_DISMISS_THRESHOLD], [1, 0]);
    // Blur: 0px at 0px, 8px at threshold
    const blur = useTransform(dragDistance, [0, DRAG_DISMISS_THRESHOLD], [0, 8]);
    const filter = useTransform(blur, (b) => `blur(${b}px)`);
    const springConfig = { type: 'spring', damping: 25, stiffness: 400 };
    const springBack = () => {
        animate(x, 0, springConfig);
        animate(y, 0, springConfig);
    };
    const animateOut = (dir) => {
        // Animate motion values to exit position while dismissing
        // popLayout removes from document flow immediately so siblings can reflow
        animate(x, dir.x, springConfig);
        animate(y, dir.y, springConfig);
        onDismiss(dir);
    };
    return (React__default.createElement(motion.div, { layout: 'position', initial: { opacity: 0, y: initialY, scale: 0.95 }, animate: { opacity: 1, y: 0, x: 0, scale: 1 }, exit: exitDir ? { opacity: 0, x: exitDir.x, y: exitDir.y } : { opacity: 0, scale: 0.95 }, transition: {
            layout: { type: 'spring', damping: 25, stiffness: 300 },
            x: { type: 'spring', damping: 25, stiffness: 400 },
            y: { type: 'spring', damping: 25, stiffness: 400 },
            opacity: { duration: 0.15 },
            scale: { duration: 0.15 },
        }, onAnimationComplete: onAnimationComplete, drag: true, dragDirectionLock: true, dragConstraints: { left: 0, right: 0, top: 0, bottom: 0 }, dragElastic: {
            left: 1,
            right: 1,
            top: isTop ? 1 : 0,
            bottom: isTop ? 0 : 1,
        }, onDragEnd: (_, info) => {
            const dir = getDismissDirection(info, isTop);
            if (dir) {
                animateOut(dir);
            }
            else {
                springBack();
            }
        }, style: { x, y, opacity, filter, touchAction: 'none' } },
        React__default.createElement(Toast, { toast: toast, state: state })));
};
function ToastRegion({ state, ...props }) {
    const ref = useRef(null);
    const regionId = useId();
    const { regionProps } = useToastRegion({ ...props, 'aria-label': `Notifications ${regionId}` }, state, ref);
    // Track toasts dismissed via drag with their exit direction
    const dragDismissed = useRef(new Map());
    // Group toasts by placement
    const toastsByPlacement = state.visibleToasts.reduce((acc, toast) => {
        const placement = toast.content.placement || 'top';
        if (!acc[placement])
            acc[placement] = [];
        acc[placement].push(toast);
        return acc;
    }, {});
    return (React__default.createElement("div", { ...regionProps, ref: ref, className: 'toast-region--rev' }, PLACEMENTS.map((placement) => {
        const isTop = placement.includes('top');
        const initialY = isTop ? -100 : 100;
        const placementToasts = toastsByPlacement[placement] || [];
        // Reverse array for bottom placements so newest appears at bottom
        const toasts = isTop ? placementToasts : [...placementToasts].reverse();
        return (React__default.createElement("div", { key: placement, className: 'toast-placement-container--rev', "data-placement": placement },
            React__default.createElement(AnimatePresence, { mode: 'popLayout' }, toasts.map((toast) => (React__default.createElement(DraggableToastItem, { key: toast.key, toast: toast, state: state, isTop: isTop, initialY: initialY, exitDir: dragDismissed.current.get(toast.key), onDismiss: (dir) => {
                    dragDismissed.current.set(toast.key, dir);
                    state.close(toast.key);
                }, onAnimationComplete: () => {
                    dragDismissed.current.delete(toast.key);
                } }))))));
    })));
}
/**
 * Individual Toast component that displays a notification.
 */
const Toast = ({ toast, state, className, ariaLabel, ...props }) => {
    const ref = useRef(null);
    const { toastProps: reactAriaToastDomAttributes, contentProps: reactAriaToastContentDomAttributes, titleProps: reactAriaToastTitleDomAttributes, descriptionProps: reactAriaToastDescriptionDomAttributes, closeButtonProps: reactAriaToastCloseButtonDomAttributes, } = useToast({ ...props, toast }, state, ref);
    const closeButtonRef = useRef(null);
    const content = toast.content;
    const variant = content.variant;
    let icon;
    switch (variant) {
        case 'success':
            icon = React__default.createElement(SuccessIcon, null);
            break;
        case 'error':
        case 'warning':
            icon = React__default.createElement(ErrorIcon, null);
            break;
        case 'info':
            icon = React__default.createElement(InfoIcon, null);
            break;
    }
    const closeToast = () => state.close(toast.key);
    const renderChildren = () => {
        if (typeof content.children === 'function') {
            return content.children(closeToast);
        }
        return content.children;
    };
    return (React__default.createElement("div", { ...reactAriaToastDomAttributes, ref: ref, "data-rev-component": 'toast', "data-rev-variant": variant, className: className, "aria-label": ariaLabel },
        React__default.createElement("div", { className: 'toast-left-section--rev' },
            React__default.createElement("div", { className: 'toast-icon--rev' }, icon)),
        React__default.createElement("div", { ...reactAriaToastContentDomAttributes, className: 'toast-content--rev' },
            React__default.createElement("div", { className: 'toast-text-content--rev' },
                content.title && (React__default.createElement("div", { ...reactAriaToastTitleDomAttributes, className: 'toast-title--rev' }, content.title)),
                content.message && (React__default.createElement("div", { ...reactAriaToastDescriptionDomAttributes, className: 'toast-message--rev' }, content.message))),
            renderChildren()),
        React__default.createElement(CloseButton, { onPress: reactAriaToastCloseButtonDomAttributes.onPress, ariaLabel: reactAriaToastCloseButtonDomAttributes['aria-label'], size: 'medium', ref: closeButtonRef, className: 'toast-close-button--rev' })));
};
const toast = ({ message, title, variant, timeout = 5000, placement = 'top', children, }) => {
    return globalToastQueue.add({ message, title, variant, placement, children }, timeout ? { timeout } : undefined);
};
const SuccessIcon = () => {
    return (React__default.createElement("svg", { xmlns: 'http://www.w3.org/2000/svg', width: '16', height: '16', viewBox: '0 0 16 16', fill: 'none' },
        React__default.createElement("path", { fillRule: 'evenodd', clipRule: 'evenodd', d: 'M1 8C1 4.13385 4.13385 1 8 1C11.8662 1 15 4.13385 15 8C15 11.8662 11.8662 15 8 15C4.13385 15 1 11.8662 1 8ZM10.5918 6.69764C10.6349 6.64024 10.666 6.57481 10.6835 6.50519C10.7009 6.43558 10.7042 6.36318 10.6933 6.29225C10.6823 6.22133 10.6573 6.15331 10.6197 6.09219C10.5821 6.03108 10.5326 5.9781 10.4742 5.93636C10.4159 5.89463 10.3497 5.86499 10.2797 5.84919C10.2097 5.83338 10.1372 5.83173 10.0666 5.84433C9.99595 5.85693 9.92853 5.88352 9.8683 5.92255C9.80808 5.96158 9.75626 6.01225 9.7159 6.07159L7.39262 9.3239L6.22667 8.15795C6.12459 8.06284 5.98958 8.01105 5.85009 8.01352C5.71059 8.01598 5.57749 8.07249 5.47884 8.17114C5.38018 8.2698 5.32367 8.4029 5.32121 8.54239C5.31875 8.68189 5.37053 8.8169 5.46564 8.91897L7.08103 10.5344C7.1363 10.5896 7.20293 10.6321 7.2763 10.659C7.34967 10.6859 7.42801 10.6965 7.50588 10.6901C7.58376 10.6837 7.6593 10.6604 7.72727 10.6218C7.79524 10.5833 7.85401 10.5304 7.89949 10.4669L10.5918 6.69764Z', fill: 'currentColor' })));
};
const ErrorIcon = () => {
    return (React__default.createElement("svg", { xmlns: 'http://www.w3.org/2000/svg', width: '14', height: '13', viewBox: '0 0 14 13', fill: 'none' },
        React__default.createElement("path", { fillRule: 'evenodd', clipRule: 'evenodd', d: 'M5.2438 1.04011C6.02428 -0.346704 7.97582 -0.346704 8.75563 1.04011L13.7257 9.87967C14.5055 11.2665 13.5297 13 11.9695 13H2.02997C0.469684 13 -0.50541 11.2665 0.274395 9.87967L5.2438 1.04011ZM7.00005 4.67842C7.13446 4.67842 7.26337 4.73321 7.35842 4.83074C7.45346 4.92827 7.50686 5.06055 7.50686 5.19847V7.79875C7.50686 7.93668 7.45346 8.06896 7.35842 8.16649C7.26337 8.26402 7.13446 8.31881 7.00005 8.31881C6.86564 8.31881 6.73673 8.26402 6.64168 8.16649C6.54664 8.06896 6.49324 7.93668 6.49324 7.79875V5.19847C6.49324 5.06055 6.54664 4.92827 6.64168 4.83074C6.73673 4.73321 6.86564 4.67842 7.00005 4.67842ZM7.00005 10.399C7.13446 10.399 7.26337 10.3442 7.35842 10.2467C7.45346 10.1492 7.50686 10.0169 7.50686 9.87897C7.50686 9.74105 7.45346 9.60877 7.35842 9.51124C7.26337 9.41371 7.13446 9.35892 7.00005 9.35892C6.86564 9.35892 6.73673 9.41371 6.64168 9.51124C6.54664 9.60877 6.49324 9.74105 6.49324 9.87897C6.49324 10.0169 6.54664 10.1492 6.64168 10.2467C6.73673 10.3442 6.86564 10.399 7.00005 10.399Z', fill: 'currentColor' })));
};
const InfoIcon = () => {
    return (React__default.createElement("svg", { xmlns: 'http://www.w3.org/2000/svg', width: '16', height: '16', viewBox: '0 0 16 16', fill: 'none' },
        React__default.createElement("path", { fillRule: 'evenodd', clipRule: 'evenodd', d: 'M1 8C1 4.13385 4.13385 1 8 1C11.8662 1 15 4.13385 15 8C15 11.8662 11.8662 15 8 15C4.13385 15 1 11.8662 1 8ZM7.25046 6.96472C8.07323 6.55333 9.0001 7.29713 8.77682 8.18954L8.2678 10.2256L8.29795 10.2113C8.42446 10.1557 8.56752 10.1508 8.69751 10.1978C8.82749 10.2447 8.93446 10.3398 8.99626 10.4634C9.05807 10.587 9.06998 10.7297 9.02953 10.8618C8.98909 10.994 8.89938 11.1055 8.77897 11.1733L8.75026 11.1891C7.92677 11.6005 6.9999 10.8567 7.22318 9.96431L7.73292 7.92821L7.70277 7.94256C7.63926 7.97786 7.56926 7.99994 7.49699 8.00747C7.42472 8.015 7.35167 8.00782 7.28225 7.98637C7.21283 7.96492 7.14846 7.92965 7.09304 7.88266C7.03761 7.83568 6.99226 7.77797 6.95973 7.713C6.92719 7.64803 6.90814 7.57715 6.90373 7.50462C6.89932 7.43209 6.90963 7.35942 6.93404 7.29099C6.95845 7.22255 6.99646 7.15976 7.04579 7.1064C7.09511 7.05305 7.15472 7.01022 7.22103 6.98051L7.25046 6.96472ZM8 5.84615C8.14281 5.84615 8.27977 5.78942 8.38075 5.68844C8.48173 5.58746 8.53846 5.4505 8.53846 5.30769C8.53846 5.16488 8.48173 5.02792 8.38075 4.92694C8.27977 4.82596 8.14281 4.76923 8 4.76923C7.85719 4.76923 7.72023 4.82596 7.61925 4.92694C7.51827 5.02792 7.46154 5.16488 7.46154 5.30769C7.46154 5.4505 7.51827 5.58746 7.61925 5.68844C7.72023 5.78942 7.85719 5.84615 8 5.84615Z', fill: 'currentColor' })));
};

export { Toast, ToastProvider, toast };
//# sourceMappingURL=Toast.js.map
